import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  Users, Clock, Calendar, CreditCard, RocketLaunch, TrendUp, ChartLineUp, Timer,
  Envelope, ClipboardText, ArrowRight,
} from "@phosphor-icons/react";

export default function AdminDashboard() {
  const [m, setM] = useState(null);
  const [audit, setAudit] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [mRes, aRes, eRes] = await Promise.all([
          api.get("/admin/dashboard"),
          api.get("/admin/audit-log?limit=20"),
          api.get("/admin/emails?limit=10"),
        ]);
        setM(mRes.data);
        setAudit(aRes.data);
        setEmails(eRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !m) return <div className="max-w-6xl mx-auto p-8 text-sm text-muted-foreground">Loading dashboard…</div>;

  const kpis = [
    { label: "New leads today", value: m.new_leads_today, icon: Users, testid: "adm-kpi-new-today" },
    { label: "Pending reviews", value: m.pending_reviews, icon: Clock, testid: "adm-kpi-pending-reviews" },
    { label: "Meetings today", value: m.meetings_today, icon: Calendar, testid: "adm-kpi-meetings-today" },
    { label: "Pending payments", value: m.pending_payments, icon: CreditCard, testid: "adm-kpi-pending-payments" },
    { label: "Active customers", value: m.active_customers, icon: RocketLaunch, testid: "adm-kpi-active-customers" },
    { label: "New this month", value: m.customers_this_month, icon: TrendUp, testid: "adm-kpi-customers-month" },
  ];

  const revenueKpis = [
    { label: "MRR (this month)", value: `${m.mrr.toLocaleString()} XOF`, hint: "Confirmed payments this month" },
    { label: "ARR (annualised)", value: `${m.arr.toLocaleString()} XOF`, hint: "MRR × 12" },
    { label: "Conversion rate", value: `${m.conversion_rate}%`, hint: "Activated / total leads" },
    { label: "Avg. onboarding", value: m.avg_onboarding_days == null ? "—" : `${m.avg_onboarding_days} days`, hint: "Request → activation" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="admin-dashboard-page">
      <PageHeader
        eyebrow="Admin"
        title="Command centre"
        subtitle="Real-time overview of leads, pipeline health, revenue signals and system activity."
        action={
          <Link to="/crm" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="adm-goto-crm">
            Open CRM <ArrowRight size={14} weight="bold" />
          </Link>
        }
      />

      {/* KPI grid */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-card border border-border rounded-xl p-4 card-elev" data-testid={k.testid}>
            <div className="flex items-center justify-between mb-3">
              <div className="h-8 w-8 rounded-md bg-teal-soft text-teal grid place-items-center">
                <k.icon size={16} weight="duotone" />
              </div>
            </div>
            <div className="data-large text-2xl font-display tracking-tight">{k.value}</div>
            <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Revenue KPIs */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {revenueKpis.map((k) => (
          <div key={k.label} className="bg-gradient-to-br from-primary to-[hsl(var(--info))] text-primary-foreground rounded-xl p-5 card-elev-lg" data-testid={`adm-rev-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-2">{k.label}</div>
            <div className="data-large text-3xl font-display tracking-tight">{k.value}</div>
            <div className="text-[11px] opacity-75 mt-1">{k.hint}</div>
          </div>
        ))}
      </div>

      {/* Lead sources */}
      {m.lead_sources?.length > 0 && (
        <div className="mt-8 bg-card border border-border rounded-2xl p-6 card-elev" data-testid="adm-lead-sources">
          <div className="flex items-center gap-2 mb-4">
            <ChartLineUp size={18} weight="duotone" className="text-teal" />
            <div className="font-display text-lg tracking-tight">Lead sources</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {m.lead_sources.map((s) => (
              <div key={s.source} className="border border-border rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{s.source}</div>
                <div className="mt-1 text-xl font-display">{s.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity + Emails */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="adm-audit-log">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardText size={18} weight="duotone" className="text-teal" />
            <div className="font-display text-lg tracking-tight">Recent activity</div>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {audit.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
            {audit.map((a) => (
              <div key={a.id} className="text-xs border-b border-border/50 pb-2">
                <div className="flex justify-between gap-2">
                  <span className="font-semibold">{a.event}</span>
                  <span className="text-muted-foreground whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                </div>
                <div className="text-muted-foreground">{a.actor_email || "system"} · {a.target_type || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="adm-emails">
          <div className="flex items-center gap-2 mb-4">
            <Envelope size={18} weight="duotone" className="text-teal" />
            <div className="font-display text-lg tracking-tight">Recent emails</div>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {emails.length === 0 && <div className="text-sm text-muted-foreground">No emails yet.</div>}
            {emails.map((e) => (
              <div key={e.id} className="text-xs border-b border-border/50 pb-2">
                <div className="flex justify-between gap-2">
                  <span className="font-semibold truncate">{e.subject}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                    e.status === "sent" ? "bg-teal-soft text-teal"
                      : e.status === "failed" ? "bg-rose-100 text-rose-700"
                      : "bg-amber-100 text-amber-700"
                  }`}>{e.status}</span>
                </div>
                <div className="text-muted-foreground truncate">→ {e.to} · {new Date(e.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
